import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Box,
  Divider,
  Typography,
  IconButton,
  Switch,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";
import {
  Close as CloseIcon,
  AccessTime,
  Settings,
  Schedule,
  Work,
  AttachMoney,
  Info,
  CheckCircle,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { shiftManagementAPI } from "../../services/api";

const ShiftFormDialog = ({ open, shift, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [formData, setFormData] = useState({
    shiftName: "",
    shiftCode: "",
    shiftBeginTime: "",
    shiftEndTime: "",
    shiftScheduleType: "regular",
    shiftDurationMinutes: "",
    punchBegin: false,
    punchEnd: false,
    beforeInMinutes: "",
    afterInMinutes: "",
    break1: false,
    break1DurationMinutes: "",
    break1BeginTime: "",
    break1EndTime: "",
    break1PunchBeginBefore: "",
    break1PunchEndAfter: "",
    break1MinDuration: "",
    break2: false,
    break2DurationMinutes: "",
    break2BeginTime: "",
    break2EndTime: "",
    break2PunchBeginBefore: "",
    break2PunchEndAfter: "",
    break2MinDuration: "",
    partialDay1On: false,
    partialDay2On: false,
    shiftBeginTimePartial: "",
    shiftEndTimePartial: "",
    shiftDurationMinutesPartial: "",
    markHalfDay: false,
    markHalfDayLeaveBefore: "report_to_office",
    otNotApplicable: false,
    shiftAllowance: "",
    shiftAllowanceOtMin: "",
    shiftAllowanceOtMax: "",
    autoPresentWfhOutdoor: false,
    markAutoPresent: false,
    isActive: true,
    categoryId: "",
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [isManuallyEditingDuration, setIsManuallyEditingDuration] = useState(false);
  const [isManuallyEditingBreak1Duration, setIsManuallyEditingBreak1Duration] = useState(false);
  const [isManuallyEditingBreak2Duration, setIsManuallyEditingBreak2Duration] = useState(false);

  // Load categories on mount
  useEffect(() => {
    const loadCategories = async () => {
      try {
        setLoadingCategories(true);
        const response = await shiftManagementAPI.getCategoriesDropdown();
        if (response.data.success) {
          setCategories(response.data.data || []);
        }
      } catch (error) {
        console.error("Error loading categories:", error);
      } finally {
        setLoadingCategories(false);
      }
    };
    if (open) {
      loadCategories();
    }
  }, [open]);

  useEffect(() => {
    if (shift && open) {
      // Convert numeric values to strings for form inputs
      const convertToString = (value) => {
        if (value === null || value === undefined) return "";
        return String(value);
      };

      setFormData({
        shiftName: shift.shiftName || shift.name || "",
        shiftCode: shift.shiftCode || shift.code || "",
        shiftBeginTime: shift.shiftBeginTime || shift.startTime || "",
        shiftEndTime: shift.shiftEndTime || shift.endTime || "",
        shiftScheduleType: shift.shiftScheduleType || shift.scheduleType || "regular",
        shiftDurationMinutes: convertToString(shift.shiftDurationMinutes || shift.durationMinutes || ""),
        punchBegin: shift.punchBegin || false,
        punchEnd: shift.punchEnd || false,
        beforeInMinutes: convertToString(shift.beforeInMinutes || ""),
        afterInMinutes: convertToString(shift.afterInMinutes || ""),
        break1: shift.break1 || false,
        break1DurationMinutes: convertToString(shift.break1DurationMinutes || ""),
        break1BeginTime: shift.break1BeginTime || "",
        break1EndTime: shift.break1EndTime || "",
        break1PunchBeginBefore: convertToString(shift.break1PunchBeginBefore || ""),
        break1PunchEndAfter: convertToString(shift.break1PunchEndAfter || ""),
        break1MinDuration: convertToString(shift.break1MinDuration || ""),
        break2: shift.break2 || false,
        break2DurationMinutes: convertToString(shift.break2DurationMinutes || ""),
        break2BeginTime: shift.break2BeginTime || "",
        break2EndTime: shift.break2EndTime || "",
        break2PunchBeginBefore: convertToString(shift.break2PunchBeginBefore || ""),
        break2PunchEndAfter: convertToString(shift.break2PunchEndAfter || ""),
        break2MinDuration: convertToString(shift.break2MinDuration || ""),
        partialDay1On: shift.partialDay1On || false,
        partialDay2On: shift.partialDay2On || false,
        shiftBeginTimePartial: shift.shiftBeginTimePartial || "",
        shiftEndTimePartial: shift.shiftEndTimePartial || "",
        shiftDurationMinutesPartial: convertToString(shift.shiftDurationMinutesPartial || ""),
        markHalfDay: shift.markHalfDay || false,
        markHalfDayLeaveBefore: shift.markHalfDayLeaveBefore || "report_to_office",
        otNotApplicable: shift.otNotApplicable || false,
        shiftAllowance: convertToString(shift.shiftAllowance || ""),
        shiftAllowanceOtMin: convertToString(shift.shiftAllowanceOtMin || ""),
        shiftAllowanceOtMax: convertToString(shift.shiftAllowanceOtMax || ""),
        autoPresentWfhOutdoor: shift.autoPresentWfhOutdoor || false,
        markAutoPresent: shift.markAutoPresent || false,
        isActive: shift.isActive !== undefined ? shift.isActive : (shift.status !== undefined ? shift.status : true),
        categoryId: shift.categoryId || shift.category?._id || shift.categoryId || "",
      });
    } else if (open && !shift) {
      // Reset form for new shift
      setFormData({
        shiftName: "",
        shiftCode: "",
        shiftBeginTime: "09:00",
        shiftEndTime: "17:00",
        shiftScheduleType: "regular",
        shiftDurationMinutes: "",
        punchBegin: false,
        punchEnd: false,
        beforeInMinutes: "",
        afterInMinutes: "",
        break1: false,
        break1DurationMinutes: "",
        break1BeginTime: "",
        break1EndTime: "",
        break1PunchBeginBefore: "",
        break1PunchEndAfter: "",
        break1MinDuration: "",
        break2: false,
        break2DurationMinutes: "",
        break2BeginTime: "",
        break2EndTime: "",
        break2PunchBeginBefore: "",
        break2PunchEndAfter: "",
        break2MinDuration: "",
        partialDay1On: false,
        partialDay2On: false,
        shiftBeginTimePartial: "",
        shiftEndTimePartial: "",
        shiftDurationMinutesPartial: "",
        markHalfDay: false,
        markHalfDayLeaveBefore: "report_to_office",
        otNotApplicable: false,
        shiftAllowance: "",
        shiftAllowanceOtMin: "",
        shiftAllowanceOtMax: "",
        autoPresentWfhOutdoor: false,
        markAutoPresent: false,
        isActive: true,
        categoryId: "",
      });
    }
    setErrors({});
  }, [shift, open]);

  // Helper function to calculate duration in minutes
  const calculateDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return null;
    try {
      const start = new Date(`2000-01-01T${startTime}`);
      const end = new Date(`2000-01-01T${endTime}`);
      let diff = (end - start) / 60000; // minutes
      if (diff < 0) diff += 1440; // Handle overnight shifts
      return diff > 0 ? Math.round(diff) : null;
    } catch (error) {
      return null;
    }
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    
    // Track manual edits to duration fields
    if (name === "shiftDurationMinutes") {
      setIsManuallyEditingDuration(true);
    } else if (name === "break1DurationMinutes") {
      setIsManuallyEditingBreak1Duration(true);
    } else if (name === "break2DurationMinutes") {
      setIsManuallyEditingBreak2Duration(true);
    }
    
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Auto-calculate main shift duration when times change
  useEffect(() => {
    if (formData.shiftBeginTime && formData.shiftEndTime && !isManuallyEditingDuration) {
      const calculatedDuration = calculateDuration(formData.shiftBeginTime, formData.shiftEndTime);
      if (calculatedDuration !== null) {
        setFormData((prev) => ({
          ...prev,
          shiftDurationMinutes: calculatedDuration.toString(),
        }));
      }
    }
  }, [formData.shiftBeginTime, formData.shiftEndTime, isManuallyEditingDuration]);

  // Auto-calculate Break 1 duration when times change
  useEffect(() => {
    if (
      formData.break1 &&
      formData.break1BeginTime &&
      formData.break1EndTime &&
      !isManuallyEditingBreak1Duration
    ) {
      const calculatedDuration = calculateDuration(formData.break1BeginTime, formData.break1EndTime);
      if (calculatedDuration !== null) {
        setFormData((prev) => ({
          ...prev,
          break1DurationMinutes: calculatedDuration.toString(),
        }));
      }
    }
  }, [formData.break1, formData.break1BeginTime, formData.break1EndTime, isManuallyEditingBreak1Duration]);

  // Auto-calculate Break 2 duration when times change
  useEffect(() => {
    if (
      formData.break2 &&
      formData.break2BeginTime &&
      formData.break2EndTime &&
      !isManuallyEditingBreak2Duration
    ) {
      const calculatedDuration = calculateDuration(formData.break2BeginTime, formData.break2EndTime);
      if (calculatedDuration !== null) {
        setFormData((prev) => ({
          ...prev,
          break2DurationMinutes: calculatedDuration.toString(),
        }));
      }
    }
  }, [formData.break2, formData.break2BeginTime, formData.break2EndTime, isManuallyEditingBreak2Duration]);

  // Reset manual edit flags when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setIsManuallyEditingDuration(false);
      setIsManuallyEditingBreak1Duration(false);
      setIsManuallyEditingBreak2Duration(false);
    }
  }, [open]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.shiftName.trim()) {
      newErrors.shiftName = "Shift name is required";
    }

    if (!formData.shiftCode.trim()) {
      newErrors.shiftCode = "Shift code is required";
    }

    if (!formData.shiftBeginTime) {
      newErrors.shiftBeginTime = "Shift begin time is required";
    }

    if (!formData.shiftEndTime) {
      newErrors.shiftEndTime = "Shift end time is required";
    }

    if (!formData.shiftDurationMinutes || formData.shiftDurationMinutes <= 0) {
      newErrors.shiftDurationMinutes = "Valid duration is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      // Map form data to API payload
      const payload = {
        name: formData.shiftName,
        code: formData.shiftCode,
        startTime: formData.shiftBeginTime,
        endTime: formData.shiftEndTime,
        durationMinutes: parseInt(formData.shiftDurationMinutes) || 0,
        scheduleType: formData.shiftScheduleType,
        punchBegin: formData.punchBegin,
        punchEnd: formData.punchEnd,
        beforeInMinutes: formData.beforeInMinutes ? parseInt(formData.beforeInMinutes) : undefined,
        afterInMinutes: formData.afterInMinutes ? parseInt(formData.afterInMinutes) : undefined,
        break1: formData.break1,
        break1DurationMinutes: formData.break1DurationMinutes ? parseInt(formData.break1DurationMinutes) : undefined,
        break1BeginTime: formData.break1BeginTime || undefined,
        break1EndTime: formData.break1EndTime || undefined,
        break1PunchBeginBefore: formData.break1PunchBeginBefore ? parseInt(formData.break1PunchBeginBefore) : undefined,
        break1PunchEndAfter: formData.break1PunchEndAfter ? parseInt(formData.break1PunchEndAfter) : undefined,
        break1MinDuration: formData.break1MinDuration ? parseInt(formData.break1MinDuration) : undefined,
        break2: formData.break2,
        break2DurationMinutes: formData.break2DurationMinutes ? parseInt(formData.break2DurationMinutes) : undefined,
        break2BeginTime: formData.break2BeginTime || undefined,
        break2EndTime: formData.break2EndTime || undefined,
        break2PunchBeginBefore: formData.break2PunchBeginBefore ? parseInt(formData.break2PunchBeginBefore) : undefined,
        break2PunchEndAfter: formData.break2PunchEndAfter ? parseInt(formData.break2PunchEndAfter) : undefined,
        break2MinDuration: formData.break2MinDuration ? parseInt(formData.break2MinDuration) : undefined,
        partialDay1On: formData.partialDay1On,
        partialDay2On: formData.partialDay2On,
        shiftBeginTimePartial: formData.shiftBeginTimePartial || undefined,
        shiftEndTimePartial: formData.shiftEndTimePartial || undefined,
        shiftDurationMinutesPartial: formData.shiftDurationMinutesPartial ? parseInt(formData.shiftDurationMinutesPartial) : undefined,
        markHalfDay: formData.markHalfDay,
        markHalfDayLeaveBefore: formData.markHalfDayLeaveBefore,
        otNotApplicable: formData.otNotApplicable,
        shiftAllowance: formData.shiftAllowance ? parseFloat(formData.shiftAllowance) : undefined,
        shiftAllowanceOtMin: formData.shiftAllowanceOtMin ? parseInt(formData.shiftAllowanceOtMin) : undefined,
        shiftAllowanceOtMax: formData.shiftAllowanceOtMax ? parseInt(formData.shiftAllowanceOtMax) : undefined,
        autoPresentWfhOutdoor: formData.autoPresentWfhOutdoor,
        markAutoPresent: formData.markAutoPresent,
        isActive: formData.isActive,
        categoryId: formData.categoryId || undefined,
      };

      if (shift) {
        await shiftManagementAPI.update(shift._id, payload);
        enqueueSnackbar("Shift updated successfully", { variant: "success" });
      } else {
        await shiftManagementAPI.create(payload);
        enqueueSnackbar("Shift created successfully", { variant: "success" });
      }

      onClose(true);
    } catch (error) {
      console.error("Error saving shift:", error);
      enqueueSnackbar(
        error.response?.data?.message || "Failed to save shift",
        { variant: "error" }
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => onClose(false)}
      maxWidth="md"
      fullWidth
      PaperProps={{
        className: "shift-dialog",
        sx: {
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {shift ? "Edit Shift" : "Create New Shift"}
          </Typography>
          <IconButton onClick={() => onClose(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ overflowY: "auto", p: 3 }}>
        <Box className="shift-form">
          {/* General Section */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, mt: 1 }}>
            <AccessTime color="primary" />
            <Typography variant="subtitle1" className="section-title" sx={{ fontWeight: 600 }}>
              General Information
            </Typography>
          </Box>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Shift Name"
                name="shiftName"
                value={formData.shiftName}
                onChange={handleChange}
                required
                error={!!errors.shiftName}
                helperText={errors.shiftName}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Shift Code"
                name="shiftCode"
                value={formData.shiftCode}
                onChange={handleChange}
                required
                error={!!errors.shiftCode}
                helperText={errors.shiftCode}
                placeholder="e.g., MORN"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  name="categoryId"
                  value={formData.categoryId}
                  onChange={handleChange}
                  label="Category"
                  disabled={loadingCategories}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category._id} value={category._id}>
                      {category.categoryName || category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Shift Begin Time"
                name="shiftBeginTime"
                type="time"
                value={formData.shiftBeginTime}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                required
                error={!!errors.shiftBeginTime}
                helperText={errors.shiftBeginTime}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Shift End Time"
                name="shiftEndTime"
                type="time"
                value={formData.shiftEndTime}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                required
                error={!!errors.shiftEndTime}
                helperText={errors.shiftEndTime}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Shift Duration (Minutes)"
                name="shiftDurationMinutes"
                type="number"
                value={formData.shiftDurationMinutes}
                onChange={handleChange}
                onFocus={() => setIsManuallyEditingDuration(true)}
                onBlur={() => {
                  // Recalculate if field is cleared or invalid
                  if (!formData.shiftDurationMinutes && formData.shiftBeginTime && formData.shiftEndTime) {
                    setIsManuallyEditingDuration(false);
                  }
                }}
                required
                error={!!errors.shiftDurationMinutes}
                helperText={
                  errors.shiftDurationMinutes || 
                  (isManuallyEditingDuration 
                    ? "You can manually override the calculated duration" 
                    : "Auto-calculated from start/end time")
                }
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl component="fieldset" fullWidth>
                <FormLabel component="legend" sx={{ fontWeight: 500, mb: 1 }}>
                  Shift Schedule Type
                </FormLabel>
                <RadioGroup
                  name="shiftScheduleType"
                  value={formData.shiftScheduleType}
                  onChange={handleChange}
                  row
                  sx={{ gap: 2 }}
                >
                  <FormControlLabel
                    value="regular"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Regular
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Works within given Time In and Time Out
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="flexible"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Flexible
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Works within same day, no need to give Time In & Out
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="break"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Break
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Works within given Time In and Time Out
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>

          {/* Punch Settings */}
          <Box mt={4}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <Settings color="primary" />
              <Typography variant="subtitle1" className="section-title" sx={{ fontWeight: 600 }}>
                Punch Settings
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="punchBegin"
                      checked={formData.punchBegin}
                      onChange={handleChange}
                    />
                  }
                  label="Punch Begin"
                />
                {formData.punchBegin && (
                  <TextField
                    fullWidth
                    label="Before In Minutes"
                    name="beforeInMinutes"
                    type="number"
                    value={formData.beforeInMinutes}
                    onChange={handleChange}
                    size="small"
                    sx={{ mt: 1 }}
                  />
                )}
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="punchEnd"
                      checked={formData.punchEnd}
                      onChange={handleChange}
                    />
                  }
                  label="Punch End"
                />
                {formData.punchEnd && (
                  <TextField
                    fullWidth
                    label="After In Minutes"
                    name="afterInMinutes"
                    type="number"
                    value={formData.afterInMinutes}
                    onChange={handleChange}
                    size="small"
                    sx={{ mt: 1 }}
                  />
                )}
              </Grid>
            </Grid>
          </Box>

          {/* Break Settings */}
          <Box mt={4}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <Schedule color="primary" />
              <Typography variant="subtitle1" className="section-title" sx={{ fontWeight: 600 }}>
                Break Settings
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />

            {/* Break 1 */}
            <FormControlLabel
              control={
                <Checkbox
                  name="break1"
                  checked={formData.break1}
                  onChange={handleChange}
                />
              }
              label={
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Break 1
                </Typography>
              }
            />

            {formData.break1 && (
              <Box sx={{ mt: 2, ml: 4, p: 2, bgcolor: "background.default", borderRadius: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Begin Time"
                    name="break1BeginTime"
                    type="time"
                    value={formData.break1BeginTime}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="End Time"
                    name="break1EndTime"
                    type="time"
                    value={formData.break1EndTime}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Duration (Minutes)"
                    name="break1DurationMinutes"
                    type="number"
                    value={formData.break1DurationMinutes}
                    onChange={handleChange}
                    onFocus={() => setIsManuallyEditingBreak1Duration(true)}
                    onBlur={() => {
                      if (!formData.break1DurationMinutes && formData.break1BeginTime && formData.break1EndTime) {
                        setIsManuallyEditingBreak1Duration(false);
                      }
                    }}
                    size="small"
                    helperText={
                      isManuallyEditingBreak1Duration 
                        ? "Manual override" 
                        : "Auto-calculated from begin/end time"
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Punch Begin Before (Minutes)"
                    name="break1PunchBeginBefore"
                    type="number"
                    value={formData.break1PunchBeginBefore}
                    onChange={handleChange}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Punch End After (Minutes)"
                    name="break1PunchEndAfter"
                    type="number"
                    value={formData.break1PunchEndAfter}
                    onChange={handleChange}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Minimum Duration (Minutes)"
                    name="break1MinDuration"
                    type="number"
                    value={formData.break1MinDuration}
                    onChange={handleChange}
                    size="small"
                  />
                  </Grid>
                </Grid>
              </Box>
            )}
            
            {/* Break 2 */}
            <Box mt={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="break2"
                    checked={formData.break2}
                    onChange={handleChange}
                  />
                }
                label={
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    Break 2
                  </Typography>
                }
              />

              {formData.break2 && (
                <Box sx={{ mt: 2, ml: 4, p: 2, bgcolor: "background.default", borderRadius: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Begin Time"
                    name="break2BeginTime"
                    type="time"
                    value={formData.break2BeginTime}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="End Time"
                    name="break2EndTime"
                    type="time"
                    value={formData.break2EndTime}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Duration (Minutes)"
                    name="break2DurationMinutes"
                    type="number"
                    value={formData.break2DurationMinutes}
                    onChange={handleChange}
                    onFocus={() => setIsManuallyEditingBreak2Duration(true)}
                    onBlur={() => {
                      if (!formData.break2DurationMinutes && formData.break2BeginTime && formData.break2EndTime) {
                        setIsManuallyEditingBreak2Duration(false);
                      }
                    }}
                    size="small"
                    helperText={
                      isManuallyEditingBreak2Duration 
                        ? "Manual override" 
                        : "Auto-calculated from begin/end time"
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Punch Begin Before (Minutes)"
                    name="break2PunchBeginBefore"
                    type="number"
                    value={formData.break2PunchBeginBefore}
                    onChange={handleChange}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Punch End After (Minutes)"
                    name="break2PunchEndAfter"
                    type="number"
                    value={formData.break2PunchEndAfter}
                    onChange={handleChange}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Minimum Duration (Minutes)"
                    name="break2MinDuration"
                    type="number"
                    value={formData.break2MinDuration}
                    onChange={handleChange}
                    size="small"
                  />
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
          </Box>

          {/* Partial Day Settings */}
          <Box mt={4}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <Work color="primary" />
              <Typography variant="subtitle1" className="section-title" sx={{ fontWeight: 600 }}>
                Partial Day Settings
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="partialDay1On"
                      checked={formData.partialDay1On}
                      onChange={handleChange}
                    />
                  }
                  label="Partial Day 1 On"
                />
                {formData.partialDay1On && (
                  <Box sx={{ mt: 2, ml: 4, p: 2, bgcolor: "background.default", borderRadius: 1 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Begin Time"
                          name="shiftBeginTimePartial"
                          type="time"
                          value={formData.shiftBeginTimePartial}
                          onChange={handleChange}
                          InputLabelProps={{ shrink: true }}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Duration (Minutes)"
                          name="shiftDurationMinutesPartial"
                          type="number"
                          value={formData.shiftDurationMinutesPartial}
                          onChange={handleChange}
                          size="small"
                        />
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="partialDay2On"
                      checked={formData.partialDay2On}
                      onChange={handleChange}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Partial Day 2 On
                    </Typography>
                  }
                />
                {formData.partialDay2On && (
                  <Box sx={{ mt: 2, ml: 4, p: 2, bgcolor: "background.default", borderRadius: 1 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="End Time"
                        name="shiftEndTimePartial"
                        type="time"
                        value={formData.shiftEndTimePartial}
                        onChange={handleChange}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Duration (Minutes)"
                        name="shiftDurationMinutesPartial"
                        type="number"
                        value={formData.shiftDurationMinutesPartial}
                        onChange={handleChange}
                        size="small"
                      />
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="markHalfDay"
                      checked={formData.markHalfDay}
                      onChange={handleChange}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Mark Half Day
                    </Typography>
                  }
                />
                {formData.markHalfDay && (
                  <Box sx={{ mt: 2, ml: 4, p: 2, bgcolor: "background.default", borderRadius: 1 }}>
                    <FormControl component="fieldset" fullWidth>
                      <FormLabel component="legend" sx={{ fontWeight: 500, mb: 1 }}>
                        Mark Half Day Leave Before
                      </FormLabel>
                      <RadioGroup
                        name="markHalfDayLeaveBefore"
                        value={formData.markHalfDayLeaveBefore}
                        onChange={handleChange}
                        row
                        sx={{ gap: 2 }}
                      >
                        <FormControlLabel
                          value="report_to_office"
                          control={<Radio />}
                          label="Report to Office"
                        />
                        <FormControlLabel
                          value="leave_office"
                          control={<Radio />}
                          label="Leave Office"
                        />
                      </RadioGroup>
                    </FormControl>
                  </Box>
                )}
              </Grid>
            </Grid>
          </Box>

          {/* Shift Allowance */}
          <Box mt={4}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <AttachMoney color="primary" />
              <Typography variant="subtitle1" className="section-title" sx={{ fontWeight: 600 }}>
                Shift Allowance
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Shift Allowance"
                  name="shiftAllowance"
                  type="number"
                  value={formData.shiftAllowance}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="OT Min (Minutes)"
                  name="shiftAllowanceOtMin"
                  type="number"
                  value={formData.shiftAllowanceOtMin}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="OT Max (Minutes)"
                  name="shiftAllowanceOtMax"
                  type="number"
                  value={formData.shiftAllowanceOtMax}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
            </Grid>
          </Box>

          {/* Other Details */}
          <Box mt={4}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <Info color="primary" />
              <Typography variant="subtitle1" className="section-title" sx={{ fontWeight: 600 }}>
                Other Details
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="otNotApplicable"
                      checked={formData.otNotApplicable}
                      onChange={handleChange}
                    />
                  }
                  label="OT Not Applicable for this Shift, default is as per OT Settings in Shift Category"
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="autoPresentWfhOutdoor"
                      checked={formData.autoPresentWfhOutdoor}
                      onChange={handleChange}
                    />
                  }
                  label="Auto Present on Work from home, Outdoor duty etc"
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="markAutoPresent"
                      checked={formData.markAutoPresent}
                      onChange={handleChange}
                    />
                  }
                  label="Mark Auto Present without punches, if worked in this shift"
                />
              </Grid>
            </Grid>
          </Box>

          {/* Status */}
          <Box mt={4}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <CheckCircle color="primary" />
              <Typography variant="subtitle1" className="section-title" sx={{ fontWeight: 600 }}>
                Status
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      name="isActive"
                      checked={formData.isActive}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          isActive: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="Active"
                />
              </Grid>
            </Grid>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button 
          onClick={() => onClose(false)} 
          color="inherit" 
          disabled={loading}
          sx={{ minWidth: 100 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading}
          sx={{ minWidth: 140 }}
        >
          {loading ? "Saving..." : shift ? "Update Shift" : "Create Shift"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShiftFormDialog;
